import { AdminApiError, AdminClient, fleetQueueListCapFired } from "./api.js";
import { readConfig } from "./config.js";
import { ConsoleDataError, type ConsoleDataSource } from "./console/data-source.js";
import { clear, h } from "./dom.js";
import {
  defaultRouteForRole,
  destinationsForRole,
  directRoutesForRole,
  isCompanyScopedRoute,
  isFleetRoute,
  isRouteAllowed,
  isShellRoute,
  moreGroupsForRole,
  phoneNavForRole,
  primaryNavForRole,
  type NavDestination,
  type ShellRoute,
} from "./nav-registry.js";
import { renderAddCompanyPage } from "./pages/add-company.js";
import { renderAgentAccessPage } from "./pages/agent-access.js";
import { renderApprovalRevealPage } from "./pages/approval-reveal.js";
import { renderBalancesPage } from "./pages/balances.js";
import { renderCompanyChecklistPage } from "./pages/company-checklist.js";
import { renderErrorsPage } from "./pages/errors.js";
import { renderExpenseAuditPage } from "./pages/expense-audit.js";
import { renderGeneralLedgerPage } from "./pages/general-ledger.js";
import { renderCardsPage } from "./pages/cards.js";
import { renderGroupsPage } from "./pages/groups.js";
import { renderHealthPage } from "./pages/health.js";
import { renderHomePage } from "./pages/home.js";
import { renderIntercompanyPage } from "./pages/intercompany.js";
import { renderMonthsPage } from "./pages/months.js";
import { renderInvitationsPage } from "./pages/invitations.js";
import { renderOnboardingPage } from "./pages/onboarding.js";
import { renderComparePage } from "./pages/compare.js";
import { renderPriorImportPage } from "./pages/prior-import.js";
import { renderQueuePage } from "./pages/queue.js";
import { renderReceiptShelfPage } from "./pages/receipt-shelf.js";
import { renderRecurringPage } from "./pages/recurring.js";
import { renderAttachedPaymentsPage } from "./pages/attached-payments.js";
import { renderReportsPage } from "./pages/reports.js";
import { renderTaxTimePage } from "./pages/tax-time.js";
import { renderRulesPage } from "./pages/rules.js";
import { renderStatementsPage } from "./pages/statements.js";
import { renderSystemPage } from "./pages/system.js";
import { renderTokensPage } from "./pages/tokens.js";
import { renderTransactionsPage } from "./pages/transactions.js";
import { renderWaitingPage } from "./pages/waiting.js";
import {
  isReceiptContextRefusal,
  RECEIPT_CONTEXT_UNAVAILABLE_OPERATOR_COPY,
} from "./refusals.js";
import {
  defaultHomeScope,
  initialHomeScope,
  landCompanyInScope,
  scopeKey,
  type HomeScope,
} from "./scope.js";
import type {
  AdminConfig,
  Company,
  CompanyHealth,
  CompanySlug,
  DashboardGroup,
  IntercompanyCompany,
  RouteName,
  StaffRole,
} from "./types.js";
import { disclosure, errorState, focusableWithin, navGlyph, svgIcon } from "./ui/components.js";
import { countOrMoreText, countText } from "./ui/display-words.js";
import * as face from "./ui/face.js";
import type { CompanyDirectory } from "./ui/face.js";
import { bankNeedsReconnect, readerRole, reviewCards } from "./ui/facts.js";
import { failureCopy } from "./ui/failure.js";
import { advice } from "./ui/capabilities.js";
import { measuredSetOf } from "./ui/measured-claim.js";
import { publishHealth, registerNavBus, type NavParams } from "./ui/nav-bus.js";
import { paintCompanySwitcher } from "./ui/owner-switcher.js";
// rd16 U review, item 4: the To review company scope crosses to the page's
// wire-shaped option through the one sanctioned slug-to-key boundary.
import { slugText } from "./console/report-render.js";
import { setReviewScopeHint, peekReviewScopeHint } from "./ui/review-scope.js";
import { encodeScopeValue } from "./scope.js";
import { navLabel } from "./ui/sentences.js";

/** Last company the operator chose. Missing storage must not throw. */
export const LAST_COMPANY_STORAGE_KEY = "gv-books.last-company";

/** A hung company list must not block the shell forever. */
export const COMPANY_LIST_WAIT_MS = 8_000;

const HEALTH_REFRESH_MS = 30_000;
/**
 * Controls whose press must skip the read cache: Refresh, and every Try again
 * button ("retry", "retry-tile", "hub-intake-retry", "months-retry", ...).
 */
export const FRESH_READ_ACTIONS =
  '[data-action="refresh"], [data-action^="retry"], [data-action$="-retry"]';

export interface NavigateOptions {
  company?: CompanySlug;
  /** Focus a company row on Health detail. */
  focusCompany?: string;
  /** Approval ticket id for the reveal page (`/approvals/{id}`). */
  approvalId?: string;
  /** Hash/popstate already moved the URL; do not write it again. */
  fromHash?: boolean;
  /** Prefilled page values. The bus keeps them for one take. */
  params?: NavParams;
}

export interface AppHandles {
  navigate: (route: RouteName | "system", opts?: NavigateOptions) => Promise<void>;
  client: AdminClient;
  root: HTMLElement;
  /** Session-cached role once /me resolves; null until then or on failure. */
  getRole: () => StaffRole | null;
}

/** True when the API is down (5xx) or the network itself failed. */
export function isServiceUnavailable(error: unknown): boolean {
  if (error instanceof AdminApiError) return error.status >= 500;
  if (error instanceof ConsoleDataError) return error.status >= 500;
  // Network failures only — do not treat arbitrary TypeErrors from render bugs
  // as "temporarily unavailable" (would hide them behind Retry).
  if (error instanceof Error && /failed to fetch|networkerror|load failed/i.test(error.message)) {
    return true;
  }
  return false;
}

/**
 * True when the request never reached GV Books (the network itself failed),
 * as opposed to the server answering with a 5xx. 6(a): a server error must not
 * tell the person to check their connection.
 */
export function isNetworkFailure(error: unknown): boolean {
  return (
    error instanceof Error &&
    !(error instanceof AdminApiError) &&
    !(error instanceof ConsoleDataError) &&
    /failed to fetch|networkerror|load failed/i.test(error.message)
  );
}

function isForbidden(error: unknown): boolean {
  if (error instanceof AdminApiError) return error.status === 403;
  if (error instanceof ConsoleDataError) return error.status === 403;
  return false;
}

export function readLastCompany(): string | null {
  try {
    const value = localStorage.getItem(LAST_COMPANY_STORAGE_KEY);
    if (value === null || value.trim() === "") return null;
    return value;
  } catch {
    return null;
  }
}

export function writeLastCompany(slug: CompanySlug): void {
  try {
    // Storage is the wire: the slug is the key, never words.
    localStorage.setItem(LAST_COMPANY_STORAGE_KEY, slug.valueOf());
  } catch {
    // Storage can be disabled. The page still has to render.
  }
}

type HashListener = (route: ShellRoute) => void;
let hashListener: HashListener | null = null;
let hashEventsBound = false;
let writingHash = false;

/**
 * Address-bar names for routes whose internal name is not the word on screen.
 *
 * rd16 U, C10: a person typed `#activity` (the nav label) and nothing happened,
 * because the route is `transactions`. The nav registry's labels are what a
 * person reads, so the two labels that differ from their route key are aliases
 * here. This is an alias, not a rename: `#transactions` keeps working, and no
 * route key moves.
 */
const HASH_ROUTE_ALIASES: Readonly<Record<string, ShellRoute>> = {
  activity: "transactions",
  books: "general-ledger",
};

function readHashRoute(): ShellRoute | null {
  const raw = location.hash.replace(/^#/, "").split("?")[0] ?? "";
  if (raw === "") return null;
  const alias = HASH_ROUTE_ALIASES[raw.toLowerCase()];
  const route = alias ?? raw;
  return isShellRoute(route) ? route : null;
}

function writeRouteHash(route: ShellRoute): void {
  const next = `#${route}`;
  if (location.hash === next) return;
  writingHash = true;
  try {
    location.hash = next;
  } finally {
    writingHash = false;
  }
}

function onHashEvent(): void {
  if (writingHash) return;
  const route = readHashRoute();
  if (route === null) return;
  hashListener?.(route);
}

function bindHashEvents(): void {
  if (hashEventsBound) return;
  hashEventsBound = true;
  window.addEventListener("hashchange", onHashEvent);
  window.addEventListener("popstate", onHashEvent);
}

/** Mount the operator portal into `root`. Used by production main and tests. */
export function mountAdminPortal(
  root: HTMLElement,
  configOverrides: Partial<AdminConfig> = {},
  options: {
    company?: CompanySlug;
    initialRoute?: RouteName | "system";
    /** Ticket id for an initial `approval` route (`/approvals/{id}`). */
    approvalId?: string;
    /** Injectable ConsoleDataSource (tests only) — defaults to live HTTP. */
    consoleSource?: ConsoleDataSource;
    /**
     * Production boot reads location.hash. Tests leave this off so a hash
     * written by an earlier mount does not steal the next mount's route.
     */
    honorLocationHash?: boolean;
  } = {},
): AppHandles {
  const config = readConfig(configOverrides);
  const client = new AdminClient(config.apiBase, config.token, options.consoleSource);
  let company: CompanySlug | "" = options.company ?? readLastCompany() ?? "";
  const companyLabels = new Map<string, string>();
  const companyDisplayNames = new Map<string, string>();
  /**
   * The shell's ONE company directory (B2a-1). Every name the shell, the
   * switcher and Home print comes from it; `complete` is the honest answer to
   * "did the companies read succeed".
   */
  let companyDirectory: CompanyDirectory = face.createCompanyDirectory([], { complete: false });
  let companiesList: Company[] = [];
  /** True after GET /v1/admin/companies settles (ok or fail). */
  let companiesSettled = false;
  let resolveCompaniesGate: () => void = () => {};
  const companiesSettledPromise = new Promise<void>((resolve) => {
    resolveCompaniesGate = resolve;
  });
  let companiesGateDone = false;
  const companiesGate = Promise.race([
    companiesSettledPromise,
    new Promise<void>((resolve) => {
      setTimeout(resolve, COMPANY_LIST_WAIT_MS);
    }),
  ]).then(() => {
    companiesGateDone = true;
  });
  /** True when GET /v1/admin/companies failed — never silently keep a hard-coded company. */
  let companiesLoadFailed = false;
  /**
   * The companies' real ledger segments, as `GET /v1/admin/intercompany`
   * answered (rd13 L1, review round 2). A segment is a free code in the
   * company's own `company.yaml` (`contracts/ledger-conventions.md`), so it is
   * READ once per session and never derived from a slug: `syn-trust` writes
   * under `TRST`, and a slug rule would look for `SYNTRUST` and call a real
   * company "another company". Null until the read answers, and null for a
   * role that may not read the books — the page label context then falls back
   * to the slug-derived segment, which is only right when a company kept the
   * default code, and never invents a name for a segment it was not told.
   */
  let segmentRows: readonly IntercompanyCompany[] | null = null;
  let segmentsRead: Promise<void> | null = null;
  let segmentsSettled = false;
  let resolveSegmentsGate: () => void = () => {};
  const segmentsSettledPromise = new Promise<void>((resolve) => {
    resolveSegmentsGate = resolve;
  });
  /**
   * The same short wait as the company list: a page paints with the segments it
   * has, and a read that has not answered in time must not hold the console.
   */
  const segmentsGate = Promise.race([
    segmentsSettledPromise,
    new Promise<void>((resolve) => {
      setTimeout(resolve, COMPANY_LIST_WAIT_MS);
    }),
  ]);
  let groups: DashboardGroup[] = [];
  let homeScope: HomeScope = { kind: "all" };
  /**
   * The company To review is scoped to (a key, never words; "" = every
   * company). Held here so the header picker shows what the list is showing
   * (rd16 U, C15); the page keeps its own copy for its filter control.
   */
  let reviewCompany: CompanySlug | "" = "";
  const lastVisitedByScope = new Map<string, string>();
  let openGroupsCreate = false;

  // Session-cached role — presentation-only; server enforces. Never guessed.
  let role: StaffRole | null = null;
  let roleReady!: () => void;
  const whenRoleReady = new Promise<void>((resolve) => {
    roleReady = resolve;
  });
  let groupsReady!: () => void;
  const whenGroupsReady = new Promise<void>((resolve) => {
    groupsReady = resolve;
  });

  clear(root);

  const alertHost = h("div", {
    class: "alert-banner",
    role: "status",
    "data-role": "alert-banner",
    hidden: "true",
  });
  const companyName = h("p", {
    class: "topbar-company",
    "data-role": "current-company-name",
    hidden: "true",
  });
  const roleChip = h("p", { class: "role-chip", "data-role": "role-chip" });
  const companySlot = h("div", { class: "company-slot", "data-role": "company-slot" });
  const nav = h("nav", {
    class: "nav nav-skeleton",
    "aria-label": "Primary",
    "aria-busy": "true",
    "data-role": "primary-nav",
  });
  // Skeleton until /me resolves — no wrong-persona flash (§2).
  for (let i = 0; i < 4; i += 1) {
    nav.append(h("span", { class: "nav-skeleton-pill", "aria-hidden": "true" }));
  }
  const bottomNav = h("nav", {
    class: "nav bottom-nav",
    "aria-label": "Mobile",
    "data-role": "bottom-nav",
    hidden: "true",
  });
  const moreSheet = h("div", {
    class: "more-sheet",
    role: "dialog",
    "aria-label": "More",
    "aria-modal": "true",
    "data-role": "more-sheet",
    tabindex: "-1",
    hidden: "true",
  });
  // Not a navigation bar. Account-manager data-route controls for the five
  // owner-only rows live here so they exist without appearing in More,
  // the primary bar, or the phone bar.
  const directRoutes = h("div", {
    class: "direct-routes",
    "data-role": "direct-routes",
    hidden: "true",
  });
  const main = h("main", { class: "main", id: "main" });
  main.append(h("p", { class: "muted", "data-role": "shell-loading" }, ["Loading…"]));
  const shellBody = h("div", { class: "shell-body" }, [alertHost, nav, main]);

  const brandHome = h(
    "a",
    { class: "brand", href: "#home", "data-role": "brand-home" },
    ["GV Books"],
  );
  brandHome.addEventListener("click", (event) => {
    event.preventDefault();
    void navigate("home");
  });

  root.append(
    h("header", { class: "topbar" }, [
      h("div", { class: "brand-block" }, [
        brandHome,
        h("p", { class: "brand-sub" }, ["Books console"]),
      ]),
      companyName,
      companySlot,
      roleChip,
    ]),
    shellBody,
    bottomNav,
    directRoutes,
    moreSheet,
  );

  let current: RouteName | "system" = options.initialRoute ?? "queue";
  /**
   * The route most recently asked for, set before navigate's first await (so
   * before `current` moves). Null until the role's start page is asked for;
   * see the companies handler.
   */
  let lastRequestedRoute: RouteName | "system" | null = null;
  let approvalId: string | undefined = options.approvalId;
  let focusCompany: string | undefined;
  let navGeneration = 0;
  let pendingCount: number | null = null;
  let pendingCapped = false;
  let lastRefreshAt = Number.NEGATIVE_INFINITY;
  let lastHealthCompanies: CompanyHealth[] | null = null;
  /**
   * 6(b): a company switch used to leave focus on <body>, so the next Tab
   * started from the top of the document. A switch moves the page to another
   * company, so focus goes to that page's heading once it has painted.
   */
  let focusHeadingAfterNav = false;

  const companyDisplayName = (slug: CompanySlug): string | null =>
    companyDirectory.name(slug);

  const paintCompanyName = (): void => {
    // The one Company control carries the name; the old centre label would
    // duplicate it. Keep the node (and its text) for existing test hooks.
    companyName.hidden = true;
    companyName.setAttribute("hidden", "true");
    clear(companyName);
    if (company === "" || isFleetRoute(current)) return;
    companyName.append(
      document.createTextNode(face.company(company, companyDirectory, "heading")),
    );
  };

  const syncCompanyChrome = (): void => {
    paintCompanyName();
    clear(companySlot);
    if (companiesLoadFailed) {
      companySlot.append(
        h("span", {
          class: "banner banner-err",
          role: "alert",
          "data-role": "companies-load-error",
        }, ["Could not load companies"]),
      );
      return;
    }
    if (companiesSettled && companiesList.length === 0) {
      companySlot.append(
        h("span", {
          class: "muted",
          role: "status",
          "data-role": "companies-empty",
        }, ["No companies available"]),
      );
      return;
    }
    // To review is a fleet-wide list with its own company filter, but the
    // header picker stays on it (rd16 U, C15): a header that loses the company
    // control on one page reads as a different console. A choice there scopes
    // the list to that company — the page's own filter, so nothing is thrown
    // away — and All companies goes back to the fleet list. Waiting stays as it
    // was: it is a system-wait list with nothing to scope.
    if (current === "waiting") {
      companySlot.append(
        h("span", { "data-role": "all-companies", hidden: "true" }, ["All companies"]),
      );
      return;
    }
    // A hand-off from Activity names the company its card belongs to, so the
    // header shows the company the LIST is scoped to (rd16 U, C15).
    if (current === "queue") {
      const handoff = peekReviewScopeHint();
      if (handoff !== null && handoff.company !== null) {
        reviewCompany = handoff.company;
      }
    }
    if (role === null) return;
    paintCompanySwitcher({
      slot: companySlot,
      role,
      mode:
        current === "home"
          ? "home"
          : current === "queue"
            ? "company"
            : isFleetRoute(current)
              ? "fleet"
              : "company",
      scope: homeScope,
      company,
      groups: role === "owner" ? groups : [],
      companies: companiesList,
      onChange: (next) => {
        if (next === "new-group") {
          openGroupsCreate = true;
          void navigate("groups");
          return;
        }
        focusHeadingAfterNav = true;
        if (current === "queue") {
          // Stay on To review, scoped to the chosen company, or to every
          // company for the All companies entry (C15).
          reviewCompany = next.kind === "company" ? next.slug : "";
          setReviewScopeHint(
            next.kind === "company" ? { company: next.slug } : null,
          );
          void navigate("queue");
          return;
        }
        if (next.kind === "company") {
          company = next.slug;
          writeLastCompany(company);
          // A company pick moves a single-company Home scope with it. Group and
          // All-companies scopes keep their members and remember the pick
          // through lastVisitedByScope (FX/FX2).
          if (current === "home" || isFleetRoute(current) || homeScope.kind === "company") {
            homeScope = { kind: "company", slug: next.slug };
          }
          lastVisitedByScope.set(scopeKey(homeScope), company);
          if (current === "home" || isFleetRoute(current)) {
            void navigate("home", { company: next.slug });
            return;
          }
          void navigate(current, { company: next.slug });
          return;
        }
        // Group or All companies.
        homeScope = next;
        if (current === "home" || isFleetRoute(current)) {
          void navigate("home");
          return;
        }
        if (isCompanyScopedRoute(current)) {
          const landed = landCompanyInScope(
            homeScope,
            groups,
            companiesList.map((c) => c.slug),
            lastVisitedByScope,
          );
          if (landed) company = landed;
          void navigate(current);
          return;
        }
        void navigate(current);
      },
    });
    if (current === "queue") {
      // The switcher offers companies; To review also needs the way back to
      // every company, so it is added here when the groups list did not
      // publish one (rd16 U, C15). Its selected entry is the company the LIST
      // is scoped to, not the company the shell last visited.
      const select = companySlot.querySelector<HTMLSelectElement>("select");
      if (select !== null) {
        if (!Array.from(select.options).some((o) => o.value === "all")) {
          select.prepend(h("option", { value: "all" }, ["All companies"]));
        }
        select.value =
          reviewCompany === ""
            ? "all"
            : encodeScopeValue({ kind: "company", slug: reviewCompany });
        if (select.value === "") select.value = "all";
      }
    }
    if (isFleetRoute(current)) {
      // Hidden compatibility hook for the old inert fleet label. The visible
      // control is the one Company switcher above.
      companySlot.append(
        h("span", { "data-role": "all-companies", hidden: "true" }, ["All companies"]),
      );
    }
  };

  /**
   * Cold-open landing: the owner's own company (last used, else the default
   * group's first member, else the first active company). All companies stays
   * an explicit switcher choice.
   */
  const resolveInitialScope = (): void => {
    if (!companiesSettled) return;
    const preferred = options.company ?? readLastCompany();
    const resolved = initialHomeScope(companiesList, groups, preferred);
    company = resolved.company;
    homeScope = resolved.scope;
  };

  const badgeHosts = (): HTMLButtonElement[] =>
    Array.from(root.querySelectorAll<HTMLButtonElement>('button[data-route="queue"]'));

  /**
   * The fleet list the badge counts is the Worker's oldest 500, so once it
   * reaches that cap the number is a lower bound and says so (re-check E2).
   *
   * Round 10 (B9-M5): the badge's accessible name, its tooltip and the visible
   * count must be the same three facts. The page it opens says "35 things to
   * review", so the label says "things to review" too, not "questions waiting
   * on you" (which was a different number and a different noun).
   */
  const badgeSentence = (count: number | null, capped: boolean): string =>
    `${navLabel("queue")}, ${countOrMoreText(count ?? 0, !capped)} things to review across all companies`;

  const badgeAria = (count: number | null, capped: boolean): string => {
    if (count === null || count <= 0) return navLabel("queue");
    return badgeSentence(count, capped);
  };

  /** The plain scope of the number, for the badge's tooltip. */
  const badgeTitle = (count: number | null, capped: boolean): string => {
    if (count === null || count <= 0) return navLabel("queue");
    return badgeSentence(count, capped);
  };

  const syncPendingBadge = (): void => {
    const hosts = badgeHosts();
    const suppressed = pendingCount === null || pendingCount <= 0;
    for (const badge of root.querySelectorAll<HTMLElement>('[data-role="pending-badge"]')) {
      if (suppressed) {
        badge.hidden = true;
        badge.setAttribute("hidden", "true");
        clear(badge);
      } else {
        badge.hidden = false;
        badge.removeAttribute("hidden");
        badge.setAttribute("title", badgeTitle(pendingCount, pendingCapped));
        clear(badge);
        badge.append(
          document.createTextNode(countText(pendingCount ?? 0, !pendingCapped)),
        );
      }
    }
    for (const hostBtn of hosts) {
      hostBtn.setAttribute("aria-label", badgeAria(pendingCount, pendingCapped));
      hostBtn.setAttribute("title", badgeTitle(pendingCount, pendingCapped));
    }
  };

  const setPendingCount = (count: number | null, capped = false): void => {
    pendingCount = count;
    pendingCapped = count !== null && capped;
    syncPendingBadge();
  };

  /** Repaint the banner from the last health read, when there is one. */
  const repaintAlertFromLastRead = (): void => {
    if (lastHealthCompanies !== null) repaintAlert(lastHealthCompanies);
  };

  const repaintAlert = (companies: CompanyHealth[]): void => {
    // rd16 U, C17: on a page that is about ONE company, another company's broken
    // bank connection is not news about what the person is looking at — it sat
    // on every TEST Rental page naming Grand Vision Family Office. A company
    // page shows only its own company's notice; the fleet pages (Home, To
    // review, Connection status, …) keep the fleet-wide notice.
    const scoped =
      isCompanyScopedRoute(current) && company !== "" ? company : null;
    paintAlertBanner(alertHost, companies, companyDisplayName, readerRole(role), (slug) => {
      focusCompany = slug;
      // C-10: the Reconnect action is on Bank accounts; Connection status only
      // described the problem, which made "Fix it" a dead end.
      void navigate("balances", { company: slug });
    }, scoped);
  };

  /**
   * Round 10 (C-13): the shell's last fleet-health read FAILED. The previous
   * reading stays on screen, but Home and the banner must say it is the last
   * one rather than repeating it as current. Cleared by the next success.
   */
  let healthReadFailed = false;
  /**
   * The health read in flight, so a page that speaks for it can wait for it
   * (home.ts `healthReadSettled`). Resolved until the first read starts.
   */
  let healthReadSettled: Promise<void> = Promise.resolve();
  /**
   * Round 2: pages that speak for the health reading (Bank accounts' reconnect
   * block) are told when a read settles, so a Refresh click that starts a read
   * ends on that read instead of the one before it.
   */
  const healthSettledListeners = new Set<() => void>();

  /** One fleet-health read. Assigned synchronously so a page can await it. */
  const readFleetHealth = (): Promise<void> => {
    const read = (async (): Promise<void> => {
      try {
        const { companies } = await client.getFleetHealth();
        lastHealthCompanies = companies;
        publishHealth({ companies });
        repaintAlert(companies);
        healthReadFailed = false;
      } catch {
        // Banner stays as-is (don't invent a clear signal), but the reading it
        // shows is now known to be old: say so wherever it is read from.
        healthReadFailed = true;
      } finally {
        for (const listener of [...healthSettledListeners]) listener();
      }
    })();
    healthReadSettled = read;
    return read;
  };

  const refreshBadgeAndBanner = async (
    opts: { skipQueue?: boolean; force?: boolean } = {},
  ): Promise<void> => {
    const now = Date.now();
    if (!opts.force && now - lastRefreshAt < HEALTH_REFRESH_MS) return;
    lastRefreshAt = now;
    // The health read is started FIRST and its promise recorded before any
    // await, so a page rendered right after this call can wait for the reading
    // it is about to speak for (C-13).
    const healthRead = readFleetHealth();
    // Queue page loads its own pending count. Every other visit is throttled
    // unless the operator presses Refresh (force).
    if (!opts.skipQueue) {
      try {
        const { items } = await client.listQueue({ status: "pending" });
        const pending = items.filter((i) => i.status === "pending");
        // AM badge excludes escalated (Owner's work). Owner badge keeps ALL
        // pending including escalated — gauge integrity between queues. The
        // derivation is the one shared card fact (B2a-1).
        const cards = reviewCards(
          measuredSetOf(pending, !fleetQueueListCapFired(pending.length)),
          readerRole(role),
        );
        setPendingCount(cards.openable.length, fleetQueueListCapFired(pending.length));
      } catch {
        setPendingCount(null);
      }
    }
    await healthRead;
  };

  let moreOpener: HTMLElement | null = null;

  /**
   * R8-09: the sheet carries aria-modal, so Tab must not walk out of it into
   * the page behind (a real-browser review measured exactly that: Tab from the
   * last link reached hidden background controls, and Shift+Tab from Close
   * landed on <body>).
   *
   * Only the two ends belong to this handler, the same rule the drawer in
   * ui/components.ts follows: between them the browser's own order is the
   * right one, so hijacking every Tab would fight it. Reached from anywhere
   * outside the sheet, focus is pulled back in rather than left to wander.
   */
  function keepTabInsideMore(event: KeyboardEvent): void {
    if (moreSheet.hasAttribute("hidden")) return;
    // Round 2 (MEDIUM): a drawer is its own `aria-modal="true"` with its own
    // document-level trap, and it can be open underneath this sheet. When
    // focus is inside that other modal, this handler must stay out of the way.
    // The drawer's listener runs first (it was added first) and has already
    // placed focus where its own cycle says; pulling it into the sheet here
    // made Tab a permanent trap in the drawer, because every press was answered
    // by two traps that disagreed about which one was in charge.
    const activeModal =
      document.activeElement instanceof Element
        ? document.activeElement.closest('[aria-modal="true"]')
        : null;
    if (activeModal !== null && activeModal !== moreSheet) return;
    const stops = focusableWithin(moreSheet);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (first === undefined || last === undefined) {
      event.preventDefault();
      moreSheet.focus();
      return;
    }
    const active = document.activeElement;
    if (
      !(active instanceof Node) ||
      !moreSheet.contains(active) ||
      active === moreSheet
    ) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function moreKeyHandler(event: KeyboardEvent): void {
    if (event.key === "Tab") {
      keepTabInsideMore(event);
      return;
    }
    if (event.key !== "Escape") return;
    event.preventDefault();
    closeMore(true);
  }

  const closeMore = (restoreFocus = false): void => {
    moreSheet.hidden = true;
    moreSheet.setAttribute("hidden", "true");
    document.removeEventListener("keydown", moreKeyHandler);
    for (const control of root.querySelectorAll<HTMLButtonElement>('[data-action="more"]')) {
      control.setAttribute("aria-expanded", "false");
    }
    if (restoreFocus && moreOpener !== null && moreOpener.isConnected) {
      moreOpener.focus();
    }
    moreOpener = null;
  };

  const openMore = (): void => {
    moreOpener =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    moreSheet.hidden = false;
    moreSheet.removeAttribute("hidden");
    document.addEventListener("keydown", moreKeyHandler);
    for (const control of root.querySelectorAll<HTMLButtonElement>('[data-action="more"]')) {
      control.setAttribute("aria-expanded", "true");
    }
    queueMicrotask(() => {
      if (moreSheet.hasAttribute("hidden")) return;
      const first = moreSheet.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      (first ?? moreSheet).focus();
    });
  };

  const routeButton = (dest: NavDestination, withBadge: boolean): HTMLButtonElement => {
    const children: Array<Node | string> = [
      navGlyph(dest.glyph),
      h("span", { class: "nav-label" }, [dest.label]),
    ];
    if (withBadge) {
      children.push(
        h("span", {
          class: "nav-badge",
          "data-role": "pending-badge",
          hidden: "true",
        }),
      );
    }
    const btn = h(
      "button",
      {
        type: "button",
        class: "nav-btn",
        "data-route": dest.id,
        "aria-label": dest.label,
      },
      children,
    );
    btn.addEventListener("click", () => {
      closeMore();
      void navigate(dest.id);
    });
    return btn;
  };

  const moreButton = (): HTMLButtonElement => {
    const btn = h(
      "button",
      {
        type: "button",
        class: "nav-btn",
        "data-action": "more",
        "aria-label": "More",
        "aria-expanded": "false",
      },
      [navGlyph("⋯"), h("span", { class: "nav-label" }, ["More"])],
    );
    btn.addEventListener("click", () => {
      if (moreSheet.hasAttribute("hidden")) openMore();
      else closeMore();
    });
    return btn;
  };

  const paintNav = (resolved: StaffRole): void => {
    clear(nav);
    nav.classList.remove("nav-skeleton");
    nav.removeAttribute("aria-busy");
    bottomNav.hidden = false;
    bottomNav.removeAttribute("hidden");
    clear(bottomNav);
    clear(moreSheet);
    clear(directRoutes);
    clear(roleChip);
    roleChip.append(
      document.createTextNode(resolved === "owner" ? "Owner" : "Account manager"),
    );

    for (const dest of primaryNavForRole(resolved)) {
      nav.append(routeButton(dest, dest.id === "queue"));
    }
    nav.append(moreButton());

    for (const dest of phoneNavForRole(resolved)) {
      bottomNav.append(routeButton(dest, dest.id === "queue"));
    }
    bottomNav.append(moreButton());

    const panel = h("div", { class: "more-sheet-panel" });
    const close = h(
      "button",
      {
        type: "button",
        class: "btn btn-quiet",
        "data-action": "close-more",
        "aria-label": "Close",
      },
      ["Close"],
    );
    close.addEventListener("click", () => {
      closeMore();
    });
    panel.append(
      h("div", { class: "more-sheet-head" }, [h("h2", {}, ["More"]), close]),
    );
    for (const group of moreGroupsForRole(resolved)) {
      const links = h("div", { class: "more-links" });
      for (const dest of group.destinations) links.append(routeButton(dest, false));
      panel.append(
        h("section", { class: "more-group", "data-more-heading": group.heading }, [
          h("h2", {}, [group.heading]),
          links,
        ]),
      );
    }
    moreSheet.append(panel);
    for (const dest of directRoutesForRole(resolved)) {
      directRoutes.append(routeButton(dest, false));
    }
    syncPendingBadge();
  };

  const paintExitToLanding = (host: HTMLElement): void => {
    const exitLabel =
      role === "owner"
        ? `Go to ${navLabel("home")}`
        : `Go to ${navLabel("queue")}`;
    const exitRoute: RouteName = role === "owner" ? "home" : "queue";
    const exit = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "denied-exit",
        "aria-label": exitLabel,
      },
      [exitLabel],
    );
    exit.addEventListener("click", () => {
      void navigate(exitRoute);
    });
    host.append(exit);
  };

  /** True authz / role miss — must stay this copy (THE MIRROR). */
  const renderDenied = (): void => {
    clear(main);
    const panel = h("div", {
      class: "denied-state",
      role: "status",
      "data-role": "denied",
    }, [
      h("p", { class: "denied-copy" }, [
        "This isn't part of your workspace.",
      ]),
    ]);
    paintExitToLanding(panel);
    main.append(panel);
  };

  /**
   * Receipt-context refuse (Worker public string). Distinct from authz denial
   * so the AM does not conclude they lack product access. Copy stays generic —
   * no medical / Zone-1 / alert-reason names (R18O-1).
   */
  const renderReceiptContextUnavailable = (): void => {
    clear(main);
    const panel = h("div", {
      class: "denied-state",
      role: "status",
      "data-role": "receipt-context-unavailable",
    }, [
      h("p", { class: "denied-copy", "data-role": "receipt-context-unavailable-copy" }, [
        RECEIPT_CONTEXT_UNAVAILABLE_OPERATOR_COPY,
      ]),
    ]);
    paintExitToLanding(panel);
    main.append(panel);
  };

  const navigate = async (
    route: RouteName | "system",
    navOpts: NavigateOptions = {},
  ): Promise<void> => {
    // Round 2 (7): consume the focus request on EVERY exit. A navigation that
    // lands nowhere (refused, refused by the Worker, or superseded) must not
    // leave it armed for a later navigation to move focus unasked.
    const focusAfterNav = consumeFocusRequest();
    const startGeneration = navGeneration;
    lastRequestedRoute = route;
    await whenRoleReady;
    if (role === null) return;
    if (role === "owner") await whenGroupsReady;
    if ((isCompanyScopedRoute(route) || route === "home") && !companiesGateDone) {
      await companiesGate;
    }
    // The account labels on a company page name the company each ledger
    // segment belongs to, and only the intercompany read says which segment
    // that is (rd13 L1, review round 2). Wait for it like the names: until it
    // settles or the same short wait runs out.
    if ((isCompanyScopedRoute(route) || route === "home") && !segmentsSettled) {
      await segmentsGate;
    }

    if (navOpts.company) {
      company = navOpts.company;
      writeLastCompany(company);
      lastVisitedByScope.set(scopeKey(homeScope), company);
    } else if (
      role === "owner" &&
      isCompanyScopedRoute(route) &&
      // Tab-bar / System links without an explicit company → landing rule (§3.2).
      navOpts.company === undefined
    ) {
      const landed = landCompanyInScope(
        homeScope,
        groups,
        companiesList.map((c) => c.slug),
        lastVisitedByScope,
      );
      if (landed) company = landed;
    }
    if (navOpts.focusCompany) focusCompany = navOpts.focusCompany;
    if (navOpts.approvalId !== undefined) approvalId = navOpts.approvalId;

    // Record visits to company-scoped surfaces for the landing rule.
    if (isCompanyScopedRoute(route)) {
      lastVisitedByScope.set(scopeKey(homeScope), company);
    }

    const markRoute = (): void => {
      current = route;
      for (const btn of root.querySelectorAll("button[data-route]")) {
        const id = btn.getAttribute("data-route");
        const isActive = id === route;
        btn.classList.toggle("active", isActive);
        if (isActive) btn.setAttribute("aria-current", "page");
        else btn.removeAttribute("aria-current");
      }
      // A page opened from More (including Admin and its list pages) keeps the
      // More tab lit. Primary tabs light themselves instead.
      const primaryIds =
        role === null ? new Set<string>() : new Set(primaryNavForRole(role).map((d) => d.id));
      const moreActive = !primaryIds.has(route);
      for (const control of root.querySelectorAll<HTMLButtonElement>('[data-action="more"]')) {
        control.classList.toggle("active", moreActive);
      }
      if (!navOpts.fromHash) writeRouteHash(route);
      syncCompanyChrome();
      // rd16 U, C17: the banner's scope depends on the page, so it is repainted
      // from the last health read on every route change — otherwise a fleet
      // notice painted on Home would still be sitting over a company page, and
      // a company's own notice would never appear until the next throttled read.
      repaintAlertFromLastRead();
    };

    if (!isRouteAllowed(role, route)) {
      markRoute();
      closeMore();
      renderDenied();
      if (navGeneration === startGeneration) focusPageHeading(focusAfterNav);
      return;
    }

    markRoute();
    const generation = ++navGeneration;
    const isCurrent = (): boolean => generation === navGeneration;

    clear(main);
    closeMore();
    // Badge/banner: at most once per 30s, unless this page is the queue
    // (it owns the count) or the operator presses Refresh.
    void refreshBadgeAndBanner({
      skipQueue: route === "queue",
      force: route === "queue",
    });

    try {
      switch (route) {
        case "home":
          await renderHomePage({
            client,
            root: main,
            scope: homeScope,
            groups,
            companySlugs: companiesList.map((c) => c.slug),
            companyLabels,
            companyNames: companyDisplayNames,
            // The shell's ONE directory (B2a-1, round 2 M1): Home reads the
            // heading from it directly, so an unnamed company reads "Company"
            // instead of treating the directory's "this company" as a name.
            companyDirectory,
            isCurrent,
            role,
            // C-13: a failed status read is said as stale, never as current.
            statusReadFailed: () => healthReadFailed,
            healthReadSettled: () => healthReadSettled,
            openAccounts: (slug) => {
              company = slug;
              lastVisitedByScope.set(scopeKey(homeScope), slug);
              void navigate("balances", { company: slug });
            },
            openReview: () => {
              // System → Workbench nav state: queue is an Owner system route.
              void navigate("queue");
            },
            openChecklist: (slug) => {
              company = slug;
              lastVisitedByScope.set(scopeKey(homeScope), slug);
              void navigate("company-checklist", { company: slug });
            },
          });
          break;
        case "waiting":
          await renderWaitingPage({
            client,
            root: main,
            isCurrent,
            role,
            companyLabels,
          });
          break;
        case "statements":
          renderStatementsPage({
            client,
            company,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "groups": {
          const startCreate = openGroupsCreate;
          openGroupsCreate = false;
          await renderGroupsPage({
            client,
            root: main,
            isCurrent,
            startCreate,
            // R8-06 (the wiring half of R8a's option): a create, edit or
            // make-default changes what the switcher offers, so the shell
            // re-reads the groups as soon as the save lands. The page calls
            // this with `void`, so it must not throw synchronously —
            // refreshOwnerGroups is async and catches its own read failure.
            onChanged: refreshOwnerGroups,
          });
          // Refresh switcher group list after management.
          void refreshOwnerGroups();
          break;
        }
        case "cards":
          await renderCardsPage({ client, root: main, isCurrent });
          break;
        case "reports":
          await renderReportsPage({
            client,
            company,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "tax-time":
          await renderTaxTimePage({
            client,
            company,
            root: main,
            role,
            isCurrent,
          });
          break;
        case "intercompany":
          await renderIntercompanyPage({
            client,
            root: main,
            companyLabels,
            isCurrent,
          });
          break;
        case "general-ledger":
          await renderGeneralLedgerPage({
            client,
            company,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "months":
          await renderMonthsPage({
            client,
            company,
            root: main,
            isCurrent,
            role,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "transactions":
          await renderTransactionsPage({
            client,
            company,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "balances":
          await renderBalancesPage({
            client,
            company,
            root: main,
            isCurrent,
            statusReadFailed: () => healthReadFailed,
            // Round 2 (2): speak for the reading the shell has or is taking,
            // and repaint when a Refresh's read lands.
            healthReadSettled: () => healthReadSettled,
            readHealth: () => readFleetHealth(),
            onHealthSettled: (listener) => {
              healthSettledListeners.add(listener);
              return () => {
                healthSettledListeners.delete(listener);
              };
            },
          });
          // Connect is an owner write. The Accounts page still paints the
          // control; this shell removes it for the account manager until the
          // Accounts lane hides it with currentRole(). Removing it also keeps
          // the page from issuing link invitations.
          if (role === "account_manager") {
            main.querySelector('[data-role="connect-account"]')?.remove();
          }
          break;
        case "queue":
          await renderQueuePage({
            client,
            operatorId: config.operatorId,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
            onPendingCount: setPendingCount,
            navigateTo: (r, o) => navigate(r, o),
            role,
            // rd16 U review, item 4: ONE source of truth for the To review
            // company scope. The shell holds it, hands it to the list, and
            // repaints the header when the page's own filter changes — so the
            // header can never say a company the list is not showing.
            companyFilter: slugText(reviewCompany),
            reviewScope: reviewCompany,
            onCompanyFilterChange: (slug) => {
              reviewCompany = slug;
              syncCompanyChrome();
            },
          });
          break;
        case "receipt-shelf":
          await renderReceiptShelfPage({
            client,
            company,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "recurring":
          await renderRecurringPage({
            client,
            company,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "attached-payments":
          await renderAttachedPaymentsPage({
            client,
            company,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "rules":
          await renderRulesPage({
            client,
            operatorId: config.operatorId,
            company,
            root: main,
            isCurrent,
            role,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "tokens":
          await renderTokensPage({ client, root: main, isCurrent });
          break;
        case "agent-access":
          await renderAgentAccessPage({
            client,
            root: main,
            role,
            companies: companiesList,
            openApproval: (id) => {
              approvalId = id;
              void navigate("approval", { approvalId: id });
            },
            isCurrent,
          });
          break;
        case "approval":
          await renderApprovalRevealPage({
            client,
            root: main,
            approvalId,
            role,
            isCurrent,
          });
          break;
        case "invitations":
          await renderInvitationsPage({ client, root: main, isCurrent });
          break;
        case "onboarding":
          renderOnboardingPage({
            client,
            root: main,
            isCurrent,
            role,
            openAddCompany: () => {
              void navigate("add-company");
            },
            openChecklist: (slug) => {
              company = slug;
              lastVisitedByScope.set(scopeKey(homeScope), slug);
              void navigate("company-checklist", { company: slug });
            },
            // R8-07 (pairs with R6a's R6-20): registering a company from
            // Opening books must leave the switcher listing it, exactly as Add
            // company does (C-5). onRegistered hands the shell a slug and
            // reloadCompanies takes an options object, so the adapter is what
            // makes one read serve both — passing reloadCompanies itself would
            // feed a slug into the options parameter.
            //
            // Round 2 (reviewer's preferred shape): a failed re-read REJECTS.
            // The page already catches that and writes "Company list not
            // refreshed — reload the page." under its own registration banner,
            // which is where the person is looking. The shell's separate stale
            // notice said the same thing in a second place, so it is dropped on
            // this path (Add company still uses it: it navigates away, so there
            // is no page banner to carry the sentence).
            onRegistered: async () => {
              const loaded = await reloadCompanies({ keepOnFailure: true });
              if (!loaded) throw new Error("company list not refreshed");
            },
          });
          break;
        case "add-company":
          await renderAddCompanyPage({
            client,
            root: main,
            role,
            isCurrent,
            onCreated: (slug) => {
              company = slug;
              // The new company is the one the person is now working in: a
              // group or all-companies scope would hide it from the switcher.
              homeScope = { kind: "company", slug };
              lastVisitedByScope.set(scopeKey(homeScope), slug);
              writeLastCompany(slug);
              // C-5: re-read the companies (the same read the boot path uses)
              // and repaint the chrome, so the switcher lists and selects the
              // new company and Home opens on it. A failed re-read keeps the
              // old list and says so instead of silently going back to it.
              void (async (): Promise<void> => {
                const loaded = await reloadCompanies({ keepOnFailure: true });
                await navigate("company-checklist", { company: slug });
                if (!loaded) paintCompaniesStaleNotice();
              })();
            },
          });
          break;
        case "company-checklist": {
          const companyRow = companiesList.find((row) => row.slug === company);
          const label = companyLabels.get(company);
          await renderCompanyChecklistPage({
            client,
            root: main,
            company,
            role,
            isCurrent,
            ...(label === undefined ? {} : { companyLabel: label }),
            ...(companyRow === undefined ? {} : { companyType: companyRow.companyType }),
          });
          break;
        }
        case "prior-import":
          renderPriorImportPage({
            client,
            company,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
          });
          break;
        case "compare":
          await renderComparePage({
            client,
            company,
            root: main,
            isCurrent,
            companyLabels,
            companySegments: labelSegments(),
            navigateTo: (r) => navigate(r),
          });
          break;
        case "health":
          await renderHealthPage({
            client,
            root: main,
            isCurrent,
            // Round 2 (1): a finished repair must reach the banner and Home
            // too, not just this page's own re-render.
            readHealth: () => readFleetHealth(),
            ...(focusCompany ? { focusCompany } : {}),
          });
          focusCompany = undefined;
          break;
        case "expense-audit":
          await renderExpenseAuditPage({
            client,
            root: main,
            company,
            companies: companiesList,
            scope: homeScope,
            groups,
            companyLabels,
            companySegments: labelSegments(),
            isCurrent,
            role,
          });
          break;
        case "system":
          renderSystemPage({
            root: main,
            role,
            navigate: (r) => navigate(r),
          });
          break;
        case "errors":
          await renderErrorsPage({ client, root: main, isCurrent });
          break;
      }
    } catch (error) {
      if (!isCurrent()) return;
      if (isReceiptContextRefusal(error)) {
        renderReceiptContextUnavailable();
        return;
      }
      if (isForbidden(error)) {
        renderDenied();
        return;
      }
      if (isServiceUnavailable(error)) {
        if (route === "queue") setPendingCount(null);
        renderUnavailable(main, () => {
          void navigate(current);
        }, route, role, failureCopy(error, { role: readerRole(role), context: "read" }).sentence);
        // Round 2 (7): a page that could not load is where the person needs
        // their place, so its heading takes the focus whether or not this
        // navigation asked for it.
        focusPageHeading(true);
        return;
      }
      clear(main);
      main.append(
        errorState(
          "The page did not finish loading. Try again.",
          () => {
            void navigate(current);
          },
          failureCopy(error, { role: readerRole(role), context: "read" }).detail ??
            "The request did not finish.",
        ),
      );
    } finally {
      // Every exit consumes and applies the request — but only this
      // navigation's. A newer navigation has already taken the page over, and
      // it was not asked to move focus (round 2, 7).
      if (generation === navGeneration) focusPageHeading(focusAfterNav);
    }
  };

  /** Round 2 (7): read and clear the pending focus request in one act. */
  function consumeFocusRequest(): boolean {
    const wanted = focusHeadingAfterNav;
    focusHeadingAfterNav = false;
    return wanted;
  }

  /** 6(b): a company switch puts focus on the new page's heading. */
  function focusPageHeading(wanted: boolean): void {
    if (!wanted) return;
    const heading = main.querySelector<HTMLElement>("h1");
    if (heading === null) return;
    heading.tabIndex = -1;
    heading.focus();
  }

  const refreshOwnerGroups = async (): Promise<void> => {
    if (role !== "owner") return;
    try {
      const { groups: next } = await client.listDashboardGroups();
      groups = next;
      // If current scope points at a deleted/unknown group, fall back.
      if (homeScope.kind === "group") {
        const id = homeScope.groupId;
        if (!groups.some((g) => g.id === id)) {
          homeScope = defaultHomeScope(groups);
        }
      }
      syncCompanyChrome();
    } catch {
      // Non-fatal for switcher; Groups page surfaces its own error.
    }
  };

  // Refresh and every Try again button must reach the server, not the 30 s
  // read cache (rd-x2). Capture phase, so the cache is clear before the
  // button's own handler starts its reads.
  root.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(FRESH_READ_ACTIONS) === null) return;
      client.clearReadCache();
    },
    true,
  );

  root.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-action="refresh"]') === null) return;
    // A Refresh re-reads what the shell owns too (C-5): a company created in
    // another tab must appear without a page reload. A failed read keeps the
    // list already on screen.
    void reloadCompanies({ keepOnFailure: true });
    void refreshBadgeAndBanner({ force: true, skipQueue: current === "queue" });
  });

  /**
   * R8-05 (round 2): the shell's badge after a Check now press the person
   * watched. Forced, because a press can land seconds after the last ordinary
   * refresh and the 30-second throttle would otherwise answer with the reading
   * from before it. `skipQueue` on To review: that page loads its own pending
   * count, and reading the same list twice for one screen is a request the
   * budget pays for and nothing shows.
   *
   * Bursts are coalesced. A settle that arrives while a refresh is out is
   * remembered, not dropped, and runs once more when that refresh lands. Ten
   * settles in one burst therefore cost two reads at most, and the LAST settle
   * is never the one lost — which is the one whose result the person is waiting
   * to see.
   */
  const checkSettledRefresh = { inFlight: false, again: false };
  /** Read-and-clear, so the loop's condition is not a value TypeScript narrowed. */
  const takeQueuedCheckSettle = (): boolean => {
    if (!checkSettledRefresh.again) return false;
    checkSettledRefresh.again = false;
    return true;
  };
  const refreshAfterCheckSettled = async (): Promise<void> => {
    if (checkSettledRefresh.inFlight) {
      checkSettledRefresh.again = true;
      return;
    }
    checkSettledRefresh.inFlight = true;
    try {
      await refreshBadgeAndBanner({ force: true, skipQueue: current === "queue" });
      while (takeQueuedCheckSettle()) {
        await refreshBadgeAndBanner({ force: true, skipQueue: current === "queue" });
      }
    } finally {
      checkSettledRefresh.inFlight = false;
    }
  };

  registerNavBus({
    navigate: (busRoute, busOpts) => {
      const next: NavigateOptions = {};
      if (busOpts?.company !== undefined) next.company = busOpts.company;
      if (busOpts?.params !== undefined) next.params = busOpts.params;
      void navigate(busRoute, next);
    },
    currentRole: () => role,
    checkSettled: () => {
      void refreshAfterCheckSettled();
    },
  });
  hashListener = (hashRoute) => {
    if (hashRoute === current) return;
    void navigate(hashRoute, { fromHash: true });
  };
  bindHashEvents();

  /**
   * Read the company list and repaint the chrome. The boot path, the Refresh
   * button, the create-company path and a registration from Opening books all
   * use this (C-5, R8-07): a company the person just created must appear in
   * the switcher without a reload.
   *
   * `keepOnFailure` keeps the list already on screen when the re-read fails
   * (the person can still work; the caller says what happened).
   * Returns true when the read succeeded.
   */
  const reloadCompanies = async (
    reloadOpts: { resolveScope?: boolean; keepOnFailure?: boolean } = {},
  ): Promise<boolean> => {
    try {
      const { companies } = await client.listCompanies();
      companiesLoadFailed = false;
      companiesSettled = true;
      resolveCompaniesGate();
      companiesList = companies.filter((c) => c.isActive);
      companyDirectory = face.createCompanyDirectory(
        companiesList.map((c) => ({ slug: c.slug, displayName: c.displayName })),
        { complete: true },
      );
      if (companiesList.length === 0) {
        homeScope = { kind: "all" };
        company = "";
        companyLabels.clear();
        companyDisplayNames.clear();
        syncCompanyChrome();
        return true;
      }
      // The pages that still take a name map get the directory's words, so a
      // page and the shell can never disagree about a name. A company the
      // directory has no name for gets NO entry (round 2 M1): the directory's
      // "this company" sentence is not a name, and a map entry carrying it
      // would be read back as one by a page that builds its own directory.
      companyLabels.clear();
      companyDisplayNames.clear();
      for (const c of companiesList) {
        const name = companyDirectory.name(c.slug);
        if (name === null) continue;
        companyLabels.set(c.slug, name);
        companyDisplayNames.set(c.slug, name);
      }
      if (reloadOpts.resolveScope === true) resolveInitialScope();
      syncCompanyChrome();
      if (lastHealthCompanies !== null) repaintAlert(lastHealthCompanies);
      return true;
    } catch {
      if (reloadOpts.keepOnFailure === true) return false;
      companiesLoadFailed = true;
      companiesSettled = true;
      resolveCompaniesGate();
      companiesList = [];
      companyDirectory = face.createCompanyDirectory([], { complete: false });
      syncCompanyChrome();
      return false;
    }
  };

  /**
   * Read the companies' ledger segments ONCE per session (rd13 L1, review round
   * 2), in parallel with the company names. The map is built on demand —
   * `labelSegments` — because a segment is only useful with a name to put on
   * it, and the two reads answer independently.
   */
  const readCompanySegments = (): Promise<void> => {
    if (segmentsRead !== null) return segmentsRead;
    segmentsRead = (async (): Promise<void> => {
      try {
        segmentRows = (await client.getIntercompany()).companies;
      } catch {
        segmentRows = null;
      } finally {
        segmentsSettled = true;
        resolveSegmentsGate();
      }
    })();
    return segmentsRead;
  };

  /** The label context's segment -> name map, from this session's reads. */
  const labelSegments = (): ReadonlyMap<string, string> =>
    face.companySegmentNames(segmentRows, companyLabels);

  /**
   * C-5 / R8-07: the company exists but the switcher could not be re-read.
   * Said once, in the shell's own words, because the page that registered it
   * has already said the registration succeeded — and the list already on
   * screen is still true, so this is a warning about staleness, not a failure
   * of the thing the person just did.
   */
  function paintCompaniesStaleNotice(): void {
    if (main.querySelector('[data-role="companies-stale-notice"]') !== null) return;
    main.prepend(
      h("p", {
        class: "banner banner-warn",
        role: "status",
        "data-role": "companies-stale-notice",
      }, ["The company was created. Reload the page to see it."]),
    );
  }

  // ONE segment read per session, in parallel with the names it will be put on
  // (rd13 L1, review round 2). A page waits for it, so an account label names
  // the company from the first paint.
  void readCompanySegments();
  void reloadCompanies({ resolveScope: true }).then((loaded) => {
    // Repaint the page being shown, now that company names are known. It is
    // the page last asked for, not `current`: until the start page's
    // navigation passes its first await, `current` is still the default
    // route, and opening that would load To review (ten reads) only to leave
    // it for the role's start page, with the badge refresh then throttled
    // for 30 s and the badge left empty (rd-x2). Before any page was asked
    // for, the start page's own navigation paints with the names.
    if (loaded && role !== null && lastRequestedRoute !== null) void navigate(lastRequestedRoute);
  });
  // ONE /me before nav renders — never guess a role (condition 6).
  void (async () => {
    try {
      const me = await client.getMe();
      role = me.role;
      if (role === "owner") {
        try {
          const { groups: next } = await client.listDashboardGroups();
          groups = next;
          resolveInitialScope();
        } catch {
          groups = [];
          homeScope = { kind: "all" };
          resolveInitialScope();
        }
        groupsReady();
      } else {
        groupsReady();
      }
      paintNav(role);
      // The banner's control depends on the role (the owner reconnects, the
      // account manager asks the owner), so repaint it now the role is known.
      repaintAlertFromLastRead();
      roleReady();
      const hashed = options.honorLocationHash === true ? readHashRoute() : null;
      const start =
        options.initialRoute !== undefined
          ? options.initialRoute
          : (hashed ?? defaultRouteForRole(role));
      await navigate(start);
    } catch (error) {
      role = null;
      groupsReady();
      roleReady();
      if (error instanceof AdminApiError && error.status === 403) {
        renderNoRoleAssigned(root);
        return;
      }
      if (error instanceof AdminApiError && error.status === 401) {
        renderAuthFailure(root);
        return;
      }
      // 5xx / network / anything else — never invent a role.
      renderUnavailable(root, () => {
        clear(root);
        mountAdminPortal(root, configOverrides, options);
      }, undefined, null, failureCopy(error, { role: "account_manager", context: "read" }).sentence);
    }
  })();

  return {
    navigate,
    client,
    root,
    getRole: () => role,
  };
}

function paintAlertBanner(
  host: HTMLElement,
  companies: CompanyHealth[],
  nameFor: (slug: CompanySlug) => string | null,
  role: StaffRole,
  onFix: (focusCompany: CompanySlug) => void,
  /**
   * The company this page is about, when it is about one (rd16 U, C17). A
   * scoped page shows only that company's notice, so the sentence names it and
   * another company's connection is not announced on it.
   */
  scopedCompany: CompanySlug | null = null,
): void {
  // The one bank fact (B2a-1/4): a connection that needs a sign-in, never a
  // second reading of `plaidItems`.
  const unhealthy = companies.filter(
    (c) =>
      bankNeedsReconnect(c) &&
      (scopedCompany === null || c.company === scopedCompany),
  );
  const canFix = role === "owner";
  clear(host);
  if (unhealthy.length === 0) {
    host.hidden = true;
    host.setAttribute("hidden", "true");
    return;
  }
  host.hidden = false;
  host.removeAttribute("hidden");
  const names: string[] = [];
  for (const row of unhealthy) {
    const named = nameFor(row.company);
    if (named !== null && !names.includes(named)) names.push(named);
  }
  const message =
    names.length === 0
      ? "A bank connection needs reconnecting"
      : names.length === 1
        ? `Bank connection needs reconnecting for ${names[0] ?? ""}`
        : `Bank connections need reconnecting for ${names.join(" and ")}`;
  // The owner can reconnect; the account manager cannot (the reconnect action
  // issues a bank invitation, which is the owner's). So the manager is told who
  // can, and gets no control that would fail (C-10, rd-z-fixes-background §3).
  if (!canFix) {
    host.append(
      h("span", { class: "banner-icon", "aria-hidden": "true" }, [svgIcon("alert")]),
      document.createTextNode(`${message}. ${advice("reconnect_bank", role)}`),
    );
    return;
  }
  const fix = h(
    "button",
    {
      type: "button",
      class: "btn btn-quiet",
      "data-action": "alert-details",
      "aria-label": "Fix it",
    },
    ["Fix it"],
  );
  fix.addEventListener("click", () => {
    const slug = unhealthy[0]?.company;
    if (slug) onFix(slug);
  });
  host.append(
    h("span", { class: "banner-icon", "aria-hidden": "true" }, [svgIcon("alert")]),
    document.createTextNode(message),
    fix,
  );
}

/**
 * Tier-3: clear "temporarily unavailable — retry" instead of a blank/generic
 * error.
 *
 * 6(a): the sentence says what actually happened. A server error (5xx) is
 * "That did not load. Try again."; only a request that never reached GV Books
 * blames the connection. The page keeps its heading, so the person still knows
 * where they are (the old panel was a bare banner with no title).
 */
function renderUnavailable(
  root: HTMLElement,
  onRetry: () => void,
  route?: RouteName | "system",
  role?: StaffRole | null,
  copy?: string,
): void {
  clear(root);
  const retry = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "retry",
      "aria-label": "Retry loading this page",
    },
    ["Retry"],
  );
  retry.addEventListener("click", onRetry);
  const title = route === undefined ? null : routeLabel(route, role ?? null);
  const children: Array<HTMLElement> = [];
  if (title !== null) children.push(h("h1", { class: "page-title" }, [title]));
  children.push(
    h("div", {
      class: "banner banner-err degraded",
      role: "alert",
      "data-role": "unavailable",
    }, [
      h("strong", {}, ["Temporarily unavailable"]),
      h("p", {}, [
        copy ?? failureCopy(undefined, { role: readerRole(role), context: "read" }).sentence,
      ]),
      retry,
    ]),
  );
  root.append(...children);
}

/** The page's own name, from the one navigation registry. */
function routeLabel(route: RouteName | "system", role: StaffRole | null): string | null {
  const found = destinationsForRole(role ?? "owner").find((d) => d.id === route);
  return found?.label ?? null;
}

/** Unmapped identity (403 on /me) — full-screen, no retry theater (§2). */
function renderNoRoleAssigned(root: HTMLElement): void {
  clear(root);
  root.append(
    h("div", {
      class: "entry-state",
      role: "status",
      "data-role": "no-role",
    }, [
      h("h1", { class: "page-title" }, ["No role assigned"]),
      h("p", { class: "lede" }, [
        "You're signed in, but no role is assigned. Ask GV for access.",
      ]),
    ]),
  );
}

/** /me 401 — auth failure (condition 6). */
function renderAuthFailure(root: HTMLElement): void {
  clear(root);
  root.append(
    h("div", {
      class: "entry-state",
      role: "alert",
      "data-role": "auth-failure",
    }, [
      h("h1", { class: "page-title" }, ["Sign-in required"]),
      h("p", { class: "lede" }, [
        "Your sign-in does not have access to GV Books. Sign in again, or ask GV for access.",
      ]),
    ]),
  );
}

/** Browser entry: mount into #app when DOM is ready. */
export function boot(): void {
  const el = document.getElementById("app");
  if (!el) throw new Error("Admin portal: #app missing");
  try {
    mountAdminPortal(el, {}, { honorLocationHash: true });
  } catch (error) {
    renderConfigError(el, error);
  }
}

/**
 * T1-3: readConfig()/mountAdminPortal() throw when operatorId is absent
 * (e.g. local `npm run dev` with no injected window.__GV_BOOKS_ADMIN__).
 * Without this boundary that's a blank white page with no error. Render a
 * clear, visible banner into `#app` instead. The admin bearer is not
 * browser config — the Pages Function attaches it.
 */
function renderConfigError(el: HTMLElement, error: unknown): void {
  clear(el);
  // Round 11 item 24: a fixed sentence on the face; the raw message is
  // developer detail behind "Show details" (B2a-8, round 2 LOW).
  const detail = failureCopy(error, {
    role: "owner",
    context: "read",
    localDetail: true,
  }).detail;
  el.append(
    h("div", { class: "banner banner-err config-error", role: "alert" }, [
      h("strong", {}, ["Admin portal: missing configuration"]),
      h("p", {}, ["The console could not start because its settings are missing."]),
      h("p", { class: "muted" }, [
        "Set window.__GV_BOOKS_ADMIN__ (operatorId) before the app script runs. ",
        "For local dev, copy admin-portal/.dev.vars.example to admin-portal/.dev.vars and run npm run dev.",
      ]),
      detail === null
        ? h("span", {})
        : disclosure("Show details", h("p", {}, [detail])),
    ]),
  );
}
